import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  IconButton,
  Collapse,
  Pagination,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  ListItemText,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useShopifyListTableStyles } from './useShopifyListTableStyles';
import { TABLE_CONSTANTS, PAGE_PER_COUNT_OPTIONS } from '../../../constants/lang';
import { useDispatch } from 'react-redux';
import { fetchCategories } from '../../../store/reducers/createProfile';
import RichTextRenderer from '../RichTextRenderer';
import WanderobeLogoNoImagePlaceholder from '../../../assets/images/WanderobeLogoNoImagePlaceholder.svg';

// Custom dropdown component for table cells
const TableDropdown = ({
  options,
  value,
  placeholder,
  disabled = false,
  isLoading = false,
  dropdownId,
  openDropdown,
  setOpenDropdown,
  isMapped = false,
  multiselect = false,
  onSelectionChange,
  suggestedValue = null
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const dropdownRef = useRef(null);

  const isOpen = openDropdown === dropdownId;

  // Initialize selected items based on value
  useEffect(() => {
    if (multiselect) {
      if (Array.isArray(value) && value?.length > 0) {
        setSelectedItems(value);
      } else if (value) {
        setSelectedItems([value]);
      } else {
        setSelectedItems([]);
      }
    }
  }, [value, multiselect]);

  // Find the selected option from the options array based on value (which could be an ID or object)
  const findSelectedOption = () => {
    if (!value || value === '' || !options || options?.length === 0) {
      // If no value but we have a suggested value, try to find it
      if (suggestedValue && !multiselect) {
        if (typeof suggestedValue === 'object' && suggestedValue.id) {
          return options.find(option => option.id === suggestedValue.id);
        }
        if (typeof suggestedValue === 'string' || typeof suggestedValue === 'number') {
          // Try to find by id first
          let found = options.find(option => option.id === suggestedValue);
          if (!found) {
            // If not found by id, try to find by name
            found = options.find(option => option.name === suggestedValue);
          }
          return found;
        }
      }
      return null;
    }

    // If value is an object with id, find by id
    if (typeof value === 'object' && value.id) {
      return options.find(option => option.id === value.id);
    }

    // If value is just an ID, find by id
    if (typeof value === 'string' || typeof value === 'number') {
      return options.find(option => option.id === value);
    }

    return null;
  };

  const selectedValue = findSelectedOption();

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = () => {
      if (isOpen) {
        setOpenDropdown(null);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      // Calculate position after a small delay to ensure DOM is updated
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, setOpenDropdown, options]);

  const handleToggle = (event) => {
    event.stopPropagation();
    if (!disabled && !isLoading && !isMapped && options && options.length > 0) {
      setOpenDropdown(isOpen ? null : dropdownId);
    }
  };

  // For multiselect, use Material-UI Select component
  if (multiselect) {
    const renderValue = (selected) => {
      if (selected?.length) {
        const numTags = selected?.length;
        const limitTags = 1;

        // Convert selected IDs to option objects
        const selectedOptions = selected.map(id =>
          options?.find(option => option?.id === id)
        )?.filter(Boolean);

        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {selectedOptions?.slice(0, limitTags)?.map((option, index) => (
              <Chip
                key={index}
                label={option?.name}
                sx={{
                  maxWidth: "100px",
                  borderRadius: "4px",
                  height: "26px",
                  overflow: "hidden",
                  fontSize: "12px",
                  fontFamily: "Lexend",
                }}
              />
            ))}
            {numTags > limitTags && ` +${numTags - limitTags}`}
          </Box>
        );
      }
      return <span style={{ fontFamily: "Lexend", opacity: 0.5 }}>{placeholder}</span>;
    };

    const renderOptions = () => {
      return options?.map((val, idx) => (
        <MenuItem
          key={idx}
          value={val}
          onClick={(event) => {
            // Prevent MenuItem from closing the dropdown
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <ListItemText primary={val.name} />
          <Checkbox
            checked={selectedItems.some((item) => item === val.id)}
            onChange={(event) => {
              event.stopPropagation();
              const newValue = event.target.checked
                ? [...selectedItems, val.id]
                : selectedItems.filter((item) => item !== val.id);
              setSelectedItems(newValue);
              onSelectionChange?.(newValue, val);
            }}
            onClick={(event) => {
              // Prevent checkbox click from closing the dropdown
              event.stopPropagation();
            }}
          />
        </MenuItem>
      ));
    };

    return (
      <Box sx={{ position: 'relative', width: '100%' }} ref={dropdownRef}>
        {isMapped ? (
          <Tooltip
            title="This product is already added to your inventory"
            placement="top"
            arrow
          >
            <Box>
              <Select
                disabled={true}
                displayEmpty
                fullWidth
                multiple
                value={selectedItems}
                renderValue={renderValue}
                IconComponent={KeyboardArrowDownIcon}
                sx={{
                  background: '#F5F5F5',
                  '& .MuiInputBase-root': {
                    background: '#F5F5F5',
                    padding: '0 10px !important',
                    minHeight: '32px',
                    borderRadius: '4px',
                    border: '1px solid #E0E0E0',
                  },
                  '& .MuiInputBase-input': {
                    padding: '2px 4px',
                    fontSize: '12px',
                    fontFamily: 'Lexend',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                {renderOptions()}
              </Select>
            </Box>
          </Tooltip>
        ) : (
          <Select
            disabled={disabled || isLoading}
            displayEmpty
            fullWidth
            multiple
            value={selectedItems}
            renderValue={renderValue}
            IconComponent={KeyboardArrowDownIcon}
            onClick={handleToggle}
            open={isOpen}
            onClose={(event, reason) => {
              // Only close if clicking outside or pressing escape
              // Don't close when selecting items in multiselect
              if (reason === 'escapeKeyDown' || reason === 'blur') {
                setOpenDropdown(null);
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 200,
                  overflowY: "auto",
                },
              },
            }}
            sx={{
              '& .MuiInputBase-root': {
                background: '#FFFFFF',
                padding: '0 10px !important',
                minHeight: '32px',
                borderRadius: '4px',
                border: '1px solid #E0E0E0',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#1243B5',
                },
                '&.Mui-focused': {
                  borderColor: '#1243B5',
                  borderWidth: '1px',
                },
              },
              '& .MuiInputBase-input': {
                padding: '2px 4px',
                fontSize: '12px',
                fontFamily: 'Lexend',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
          >
            {renderOptions()}
          </Select>
        )}
      </Box>
    );
  }

  // For single select, use Material-UI Select to match InventoryForm styling
  const renderSingleSelectValue = (selected) => {
    if (selected && selected.name) {
      return <span style={{ fontFamily: 'Lexend', fontSize: '14px', color: '#0F0F0F' }}>{selected.name}</span>;
    }
    return <span style={{ fontFamily: 'Lexend', fontSize: '14px', opacity: 0.5 }}>{placeholder}</span>;
  };

  const renderSingleSelectOptions = () => {
    return options?.map((val, idx) => (
      <MenuItem key={idx} value={val}>
        {val.name}
      </MenuItem>
    ));
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }} ref={dropdownRef}>
      {isMapped ? (
        <Tooltip
          title="This product is already added to your inventory"
          placement="top"
          arrow
        >
          <Box>
            <Select
              disabled={true}
              displayEmpty
              fullWidth
              value={selectedValue || ''}
              renderValue={renderSingleSelectValue}
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                background: '#F5F5F5',
                '& .MuiInputBase-root': {
                  background: '#F5F5F5',
                  padding: '0 10px !important',
                  minHeight: '40px',
                  borderRadius: '0px !important',
                  border: '1px solid #E0E0E0',
                },
                '& .MuiInputBase-input': {
                  padding: '2px 4px',
                  fontSize: '14px !important',
                  fontFamily: 'Lexend',
                  fontWeight: '400',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              {renderSingleSelectOptions()}
            </Select>
          </Box>
        </Tooltip>
      ) : (
        <Select
          disabled={disabled || isLoading}
          displayEmpty
          fullWidth
          value={selectedValue || ''}
          renderValue={renderSingleSelectValue}
          IconComponent={KeyboardArrowDownIcon}
          onChange={(event) => {
            const selectedOption = event.target.value;
            onSelectionChange?.(selectedOption?.id, selectedOption);
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 200,
                overflowY: "auto",
              },
            },
          }}
          sx={{
            '& .MuiInputBase-root': {
              background: '#FFFFFF',
              padding: '0 10px !important',
              minHeight: '40px',
              borderRadius: '0px !important',
              border: '1px solid #E0E0E0',
              cursor: 'pointer',
              '&:hover': {
                borderColor: '#1243B5',
              },
              '&.Mui-focused': {
                borderColor: 'gray',
                borderWidth: '1px',
              },
            },
            '& .MuiInputBase-input': {
              padding: '2px 4px',
              fontSize: '14px !important',
              fontFamily: 'Lexend',
              fontWeight: '400',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        >
          {renderSingleSelectOptions()}
        </Select>
      )}
    </Box>
  );
};

// Expandable row component for variants
const VariantRow = ({ variant, styles }) => (
  <TableRow>
    <TableCell sx={styles.variantTableCell}>
      <Box sx={styles.productNameCell}>
        {
          variant?.image ? (
            <Box component="img"
              src={variant?.image}
              alt="variant"
              style={styles.productImage}
            />
          ) : (
            <Box
              component="img"
              src={WanderobeLogoNoImagePlaceholder}
              alt="variant"
              style={styles.productImage}
              sx={{ objectFit: 'contain', opacity: 0.3}}
            />
          )
        }
      </Box>
    </TableCell>
    <TableCell sx={styles.variantTableCell}>{variant?.options}</TableCell>
    <TableCell sx={styles.variantTableCell}>{variant?.quantity}</TableCell>
  </TableRow>
);

// Main product row component
const ProductRow = ({
  product,
  isSelected,
  onSelect,
  expandedRows,
  onToggleExpand,
  styles,
  categories,
  isLoadingCategories,
  openDropdown,
  setOpenDropdown,
  onProductMappingChange,
  productMappings
}) => {
  const isExpanded = expandedRows.includes(product.id);

  return (
    <>
      <TableRow>
        <TableCell sx={styles.tableCell}>
          {product.isMapped ? (
            <Tooltip
              title="This product is already added to your inventory"
              placement="top"
              arrow
              enterDelay={500}
              leaveDelay={0}
            >
              <Box sx={{ display: 'inline-block' }}>
                <Checkbox
                  id={`checkbox-${product.shopifyProductId}`}
                  name={`product-${product.shopifyProductId}`}
                  checked={true}
                  disabled={true}
                  sx={styles.checkboxDisabled}
                />
              </Box>
            </Tooltip>
          ) : (
            <Checkbox
              id={`checkbox-${product.shopifyProductId}`}
              name={`product-${product.shopifyProductId}`}
              checked={isSelected}
              onChange={(e) => onSelect(product.shopifyProductId, e.target.checked)}
              sx={styles.checkbox}
            />
          )}
        </TableCell>
        <TableCell sx={styles.tableCell}>{product.shopifyProductId}</TableCell>
        <TableCell sx={styles.tableCell}>
          <Box sx={styles.productNameCell}>
            <Typography sx={styles.productNameText}>
              {product.name}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={styles.tableCell}>
          <RichTextRenderer
            content={product?.description || ''}
            maxWidth="166px"
            maxHeight="115px"
            imageMaxWidth="100%"
            imageMaxHeight="80px"
            videoMaxWidth="100%"
            videoMaxHeight="80px"
            showImages={true}
            showVideos={true}
            previewMode={true}
            previewLength={150}
            tooltipMode={true}
            sx={styles.descriptionText}
          />
        </TableCell>
        <TableCell sx={styles.tableCell}>
          <Box sx={styles.tagsContainer}>
            {product?.tags?.slice(0, 1)?.map((tag, index) => (
              <Box key={index} sx={styles.tagChip}>
                {tag}
              </Box>
            ))}
            {product?.tags?.length > 1 && (
              <Box sx={styles.moreTagsText}>
                +{product?.tags?.length - 1}
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell sx={styles.tableCell}>
          <Box sx={styles.statusActive}>
            {product.status}
          </Box>
        </TableCell>
        <TableCell sx={styles.tableCell}>${product.price}</TableCell>
        <TableCell sx={styles.tableCell}>{product.totalQuantity}</TableCell>
        <TableCell sx={styles.tableCell}>
          {product?.images?.length > 0 && (
            <Box sx={styles.imagesContainer}>
              {product?.images?.slice(0, 3)?.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`product-${index}`}
                  style={styles.smallImage}
                />
              ))}
              {product?.images?.length > 3 && (
                <Box sx={styles.moreImagesBox}>
                  +{product?.images?.length - 3}
                </Box>
              )}
            </Box>
          )}
        </TableCell>
        <TableCell sx={styles.tableCell}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ width: '100%', textAlign: 'center', fontFamily: 'Lexend', fontSize: '14px', color: '#0F0F0F' }}>
              {product.variantsCount}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onToggleExpand(product.id)}
              sx={styles.expandButton}
            >
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
        </TableCell>
        <TableCell sx={styles.tableCell}>
          <Box sx={styles.dropdownCell}>
            <TableDropdown
              options={categories}
              value={productMappings[product.id]?.categories || []}
              placeholder="Select Categories"
              disabled={false}
              isLoading={isLoadingCategories}
              dropdownId={`category-${product.id}`}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              isMapped={product.isMapped}
              multiselect={true}
              suggestedValue={product.suggestedCategories || []}
              onSelectionChange={(newSelectedItems, option) => {
                // Handle category selection change
                onProductMappingChange?.(product.id, 'categories', newSelectedItems);
              }}
            />
          </Box>
        </TableCell>
      </TableRow>

      {/* Expanded variants table */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={styles.variantTableContainer}>
              <Table size="small" sx={styles.variantTable}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={styles.variantTableHeader}>PHOTO</TableCell>
                    <TableCell sx={styles.variantTableHeader}>VARIANT</TableCell>
                    <TableCell sx={styles.variantTableHeader}>QUANTITY</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {product?.variants?.map((variant, index) => (
                    <VariantRow key={index} variant={variant} styles={styles} />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ShopifyListTable = ({
  data = [],
  onSelectionChange,
  selectedItems = [],
  pageSizeOptions = PAGE_PER_COUNT_OPTIONS,
  totalCount = 0,
  pageNo = 1,
  pageSize = 10,
  setPageNo = () => { },
  setPageSize = () => { },
  showPagination = true,
  tableLoading = false,
  onProductMappingChange,
  productMappings = {},
  headingSx = {}
}) => {
  const styles = useShopifyListTableStyles();
  const [expandedRows, setExpandedRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const getCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const res = await dispatch(fetchCategories());
        const { data, status, message } = res?.payload;
        if (status) {
          setCategories(data);
        } else {
          console.log('Categories API failed:', message);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    getCategories();
  }, [dispatch]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleSelectAll = (checked) => {
    if (checked) {
      const unmappedProductIds = data
        .filter(item => !item.isMapped)
        .map(item => item.shopifyProductId);
      const newSelectedItems = Array.from(new Set([...selectedItems, ...unmappedProductIds]));
      onSelectionChange(newSelectedItems);
    } else {
      const newSelectedItems = selectedItems.filter(id => !data.some(item => item.shopifyProductId === id));
      onSelectionChange(newSelectedItems);
    }
  };

  const handleSelectItem = (shopifyProductId, checked) => {
    if (checked) {
      const newSelectedItems = Array.from(new Set([...selectedItems, shopifyProductId]));
      onSelectionChange(newSelectedItems);
    } else {
      const newSelectedItems = selectedItems.filter(id => id !== shopifyProductId);
      onSelectionChange(newSelectedItems);
    }
  };

  const handleToggleExpand = (productId) => {
    setExpandedRows(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handlePageChange = (event, value) => {
    setPageNo(value);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    setPageSize(newPageSize);
  };

  const selectedCount = selectedItems?.length;
  const unmappedProducts = data?.filter(item => !item.isMapped);
  const isAllSelected = unmappedProducts?.length > 0 && unmappedProducts?.every(item => selectedItems.includes(item.shopifyProductId));

  let filteredOptions = pageSizeOptions?.filter(option => option <= totalCount);
  if (!filteredOptions?.includes(totalCount) && totalCount > Math.max(...filteredOptions, 0)) {
    filteredOptions?.push(parseInt(totalCount));
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.tableHeader}>
        <Box sx={styles.headerLeft}>
          <Typography variant="h6" sx={{ ...styles.headerTitle, ...headingSx }}>
            Select all listings
          </Typography>
        </Box>
        <Box sx={styles.headerRight}>
          <Typography variant="body2" sx={{ ...styles.selectionCount, ...headingSx }}>
            {selectedCount}/{totalCount} Selected
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.tableWrapper}>
        {tableLoading && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1,
            borderRadius: '8px'
          }}>
            <Typography sx={{ color: '#666666', fontSize: '14px' }}>
              Loading...
            </Typography>
          </Box>
        )}
        <Table sx={styles.mainTable}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...styles.tableHeaderCell }}>
                <Checkbox
                  id="select-all-checkbox"
                  name="select-all"
                  checked={isAllSelected}
                  indeterminate={selectedCount > 0 && selectedCount < data?.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  sx={{ ...styles.checkbox, pb: '8px', pt: '8px', pl: '8px' }}
                />
              </TableCell>
              <TableCell sx={{ ...styles.tableHeaderCell }}>SHOPIFY PRODUCT ID</TableCell>
              <TableCell sx={styles.tableHeaderCell}>NAME</TableCell>
              <TableCell sx={styles.tableHeaderCell}>DESCRIPTION</TableCell>
              <TableCell sx={styles.tableHeaderCell}>TAGS</TableCell>
              <TableCell sx={styles.tableHeaderCell}>STATUS</TableCell>
              <TableCell sx={styles.tableHeaderCell}>PRICE</TableCell>
              <TableCell sx={styles.tableHeaderCell}>TOTAL QTY.</TableCell>
              <TableCell sx={styles.tableHeaderCell}>IMAGES</TableCell>
              <TableCell sx={styles.tableHeaderCell}>VARIANTS COUNT</TableCell>
              <TableCell sx={styles.tableHeaderCell}>CATEGORY</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((product) => (
              <ProductRow
                key={product.id}
                product={product}
                isSelected={selectedItems?.includes(product.shopifyProductId)}
                onSelect={(id, checked) => handleSelectItem(product.shopifyProductId, checked)}
                expandedRows={expandedRows}
                onToggleExpand={handleToggleExpand}
                styles={styles}
                categories={categories}
                isLoadingCategories={isLoadingCategories}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
                onProductMappingChange={onProductMappingChange}
                productMappings={productMappings}
              />
            ))}
          </TableBody>
        </Table>
      </Box>

      {showPagination && data?.length > 0 && (
        <Box sx={styles.paginationContainer}>
          <Box sx={styles.pagesize}>
            <Typography sx={styles.showingText}>
              {TABLE_CONSTANTS.SHOWING}
            </Typography>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              sx={styles.showSelect}
            >
              {filteredOptions?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <Typography sx={styles.ofText}>
              {TABLE_CONSTANTS.OF} {totalCount}
            </Typography>
          </Box>
          <Pagination
            siblingCount={0}
            boundaryCount={2}
            count={totalPages}
            page={pageNo}
            onChange={handlePageChange}
            variant='outlined'
            shape='rounded'
            sx={styles.pagination}
          />
        </Box>
      )}
    </Box>
  );
};

export default ShopifyListTable; 